import { useQuery } from '@tanstack/react-query'
import { ChevronLeft } from 'lucide-react'
import {
  broadcastQueryOptions,
  broadcastDeliveryQueryOptions,
} from '@/utils/broadcastQueryOptions'
import type { BroadcastRecipient } from '@/types/broadcastTypes'
import { useShellStore } from '@/stores/shellStore'
import { getInitials } from '@/lib/format'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

type Bucket = 'delivered' | 'pending' | 'failed' | 'skipped'

function bucketFor(status: string): Bucket {
  if (status === 'failed' || status === 'undelivered') return 'failed'
  if (status === 'skipped') return 'skipped'
  if (status === 'delivered' || status === 'read') return 'delivered'
  return 'pending'
}

// Twilio refuses a number that replied STOP until that phone replies START.
const STOPPED_ERROR = '21610'

function rowDisplay(r: BroadcastRecipient): {
  badge: string
  badgeClass: string
  sub: string
  subClass: string
} {
  const bucket = bucketFor(r.status)
  if (bucket === 'skipped') {
    return {
      badge: 'Skipped',
      badgeClass: 'bg-warn-tint text-warn-ink',
      sub: 'No mobile on file',
      subClass: 'font-semibold text-warn-ink',
    }
  }
  if (bucket === 'failed') {
    if (r.errorCode === STOPPED_ERROR) {
      return {
        badge: 'Blocked',
        badgeClass: 'bg-danger-tint text-danger-ink',
        sub: 'Replied STOP. They get nothing until their phone replies START.',
        subClass: 'font-semibold text-danger-ink',
      }
    }
    return {
      badge: 'Failed',
      badgeClass: 'bg-danger-tint text-danger-ink',
      sub: r.errorMessage || 'Delivery failed',
      subClass: 'text-danger-ink',
    }
  }
  if (bucket === 'pending') {
    return {
      badge: 'Pending',
      badgeClass: 'bg-surface-2 text-muted-foreground',
      sub: r.status === 'sent' ? 'With the carrier, not confirmed yet' : 'Waiting to send',
      subClass: 'text-muted-foreground',
    }
  }
  return {
    badge: 'Delivered',
    badgeClass: 'bg-sage-tint text-sage-ink',
    sub: 'Confirmed by the carrier',
    subClass: 'font-semibold text-sage-ink',
  }
}

export function DeliveryView({ broadcastId }: { broadcastId: string }) {
  const backToThread = useShellStore((s) => s.backToThread)

  const broadcast = useQuery(broadcastQueryOptions(broadcastId))
  const { data, status } = useQuery(broadcastDeliveryQueryOptions(broadcastId))

  const name = broadcast.data?.name || 'Broadcast'
  const stats = data?.stats ?? { delivered: 0, pending: 0, failed: 0, skipped: 0 }
  const people = broadcast.data?.totalRecipients ?? 0
  const sentAt = broadcast.data?.createdAt
    ? new Date(broadcast.data.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })
    : 'Today'

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex min-h-[56px] flex-none items-center gap-2.5 border-b border-border px-[18px] py-[9px]">
        <button type="button" onClick={backToThread} className="px-0.5 text-muted-foreground" aria-label="Back">
          <ChevronLeft className="h-[22px] w-[22px]" />
        </button>
        <div>
          <div className="text-base font-extrabold">Delivery · {name}</div>
          <div className="mt-px text-[12px] text-muted-foreground">
            sent {broadcast.data?.senderName ? `by ${broadcast.data.senderName} on ` : ''}
            {sentAt} · {stats.delivered} of {people} confirmed delivered
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-[18px]">
        <div className="mx-auto max-w-[680px]">
          {/* Stat cards */}
          <div className="mb-[18px] grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              value={stats.delivered}
              label="Delivered"
              className="border-sage bg-sage-tint text-sage-ink"
            />
            <StatCard
              value={stats.pending}
              label="Pending"
              className="border-border bg-surface-2 text-foreground"
            />
            <StatCard
              value={stats.failed}
              label="Failed"
              className="border-danger bg-danger-tint text-danger-ink"
            />
            <StatCard
              value={stats.skipped}
              label="No mobile"
              className="border-warn-border bg-warn-tint text-warn-ink"
            />
          </div>

          {/* Recipient list */}
          <div className="overflow-hidden rounded-[14px] border border-border">
            {status === 'pending' ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (data?.recipients.length ?? 0) === 0 ? (
              <div className="p-6 text-center text-[13px] text-faint">No recipients.</div>
            ) : (
              data!.recipients.map((r) => {
                const d = rowDisplay(r)
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 border-b border-border-2 px-[15px] py-3 last:border-b-0"
                  >
                    <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-primary-tint text-[12px] font-bold text-primary">
                      {getInitials(r.displayName)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13.5px] font-bold text-foreground">
                        {r.displayName || 'Unknown'}
                      </div>
                      {d.sub && (
                        <div className={cn('truncate text-[11.5px]', d.subClass)}>{d.sub}</div>
                      )}
                    </div>
                    <span
                      className={cn(
                        'flex-none rounded-full px-[11px] py-1 text-[11px] font-extrabold',
                        d.badgeClass,
                      )}
                    >
                      {d.badge}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  value,
  label,
  className,
}: {
  value: number
  label: string
  className: string
}) {
  return (
    <div className={cn('rounded-[14px] border p-4 text-center', className)}>
      <div className="text-[30px] font-extrabold leading-none">{value}</div>
      <div className="mt-1.5 text-[12.5px] font-bold">{label}</div>
    </div>
  )
}
